import { Box, Button, Heading, Spinner, Text, useToast, VStack } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CustomToast from 'components/CustomToast';
import FormField from 'components/FormField';
import { useAuthContext } from 'context/authContext';
import apiService from 'services';
import { completeLoginSession } from 'utils/completeLoginSession';
import mapAuthApiError from 'utils/mapAuthApiError';
import { clearMfaSessionStorage, readMfaTotpContext } from 'utils/mfaSessionStorage';
import { MfaTotpCodeSchema } from 'validation/schemas/AuthenticationSchema';

import AuthenticationMfaShell from './AuthenticationMfaShell';

const initialValues = { code: '' };

const MfaTotpPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { onAuthSuccess } = useAuthContext();
  const [totpContext, setTotpContext] = useState<ReturnType<typeof readMfaTotpContext>>(null);

  useEffect(() => {
    const context = readMfaTotpContext();
    if (!context) {
      navigate('/', { replace: true });
      return;
    }
    setTotpContext(context);
  }, [navigate]);

  if (!totpContext) {
    return null;
  }

  const goToSignIn = () => {
    clearMfaSessionStorage();
    navigate('/', { replace: true });
  };

  return (
    <AuthenticationMfaShell
      title='Two-factor verification'
      description={
        <>
          Your account is protected with{' '}
          <Box as='span' fontWeight='bold' fontFamily='regular_roboto' display='inline'>
            multi-factor authentication
          </Box>{' '}
          (MFA).
          <br />
          <br />
          To sign in, please enter the 6-digit code from your authenticator app for{' '}
          <Box as='span' fontWeight='bold' fontFamily='regular_roboto' display='inline'>
            {totpContext.email}
          </Box>
          .
        </>
      }
    >
      <Formik
        initialValues={initialValues}
        validationSchema={MfaTotpCodeSchema}
        onSubmit={async (form, { setSubmitting }) => {
          const context = readMfaTotpContext();
          if (!context) {
            goToSignIn();
            setSubmitting(false);
            return;
          }
          try {
            const { data } = await apiService.verify_mfa({
              email: context.email,
              session: context.session,
              code: form.code,
            });
            clearMfaSessionStorage();
            completeLoginSession({
              navigate,
              onAuthSuccess,
              payload: {
                token: data.token,
                expiry: data.expiry,
                user: data.user,
                refresh_token: data.refresh_token,
                refresh_token_expiry: data.refresh_token_expiry,
              },
              gtmUserId: data.user?.id,
            });
            queryClient.invalidateQueries(['user']);
          } catch (error: any) {
            toast({
              duration: 3500,
              render: () => (
                <CustomToast
                  title={mapAuthApiError(error, 'Verification failed. Try a new code.')}
                  status='error'
                />
              ),
            });
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ errors, touched, isSubmitting }) => (
          <Form style={{ height: 'auto' }}>
            <VStack spacing={0} mb='1rem' mt={0} align='stretch' h='auto' minH={0}>
              <FormField
                label='Authentication code'
                name='code'
                type='text'
                inputHeight='auto'
                labelStyles={{
                  fontSize: '14px',
                  color: '#121826',
                }}
                inputStyles={{
                  maxWidth: '300px',
                  boxShadow: '0px 0px 0px 1px #7A849E',
                  minHeight: '2.5rem',
                  paddingBlock: '0.5rem',
                }}
                placeholder='123456'
                error={errors.code}
                touched={touched.code}
                disabled={isSubmitting}
              />
            </VStack>
            <Button
              type='submit'
              w='100%'
              maxWidth='300px'
              h='12'
              borderRadius='xs'
              fontSize='sm'
              bgColor='green.900'
              disabled={isSubmitting}
              _hover={{ bgColor: '' }}
            >
              VERIFY {isSubmitting && <Spinner ml={4} />}
            </Button>
          </Form>
        )}
      </Formik>

      <Heading
        mt='.6rem !important'
        color='black'
        display='flex'
        width='100%'
        fontFamily='regular_roboto'
      >
        <Text opacity={0.7} fontSize='2xs'>
          Need to start over?
        </Text>
        <Text
          ml={1}
          fontFamily='regular_roboto'
          fontSize='2xs'
          color='green.900'
          cursor='pointer'
          onClick={goToSignIn}
        >
          Back to sign in
        </Text>
      </Heading>
    </AuthenticationMfaShell>
  );
};

export default MfaTotpPage;
